<template>
	<li class="sidebar-link" :class="{ active, expanded, dragging, 'drag-over': dragOver, 'mode-edit': mode === 'edit', 'mode-reorder': mode === 'reorder' }" :draggable="mode === 'reorder'" @dragstart="handleDragStart" @dragend="handleDragEnd" @dragover.prevent="handleDragOver" @dragleave="handleDragLeave" @drop.prevent="handleDrop">
		<NuxtLink :to="to" class="link-content" :tabindex="mode === 'reorder' ? -1 : 0">
			<UiIcon v-if="icon" :name="icon" />
			<span class="link-title">{{ title }}</span>
		</NuxtLink>
		<button v-if="mode === 'edit'" :id="dropdownTargetId" class="more-button" type="button" @click.stop.prevent="toggleDropdown">
			<UiIcon name="ellipsisVertical" />
		</button>
		<div v-else-if="mode === 'reorder'" class="drag-handle">
			<UiIcon name="dragHandle" />
		</div>

		<UiDropdown v-model="isDropdownOpen" :target="`#${dropdownTargetId}`" align="left" position="bottom" :gap="4">
			<header class="dropdown-options-header">
				<strong>
					Options <i>{{ title }}</i>
				</strong>
			</header>
			<ul class="dropdown-options-list">
				<li>
					<button @click="handleReorderMode">
						<UiIcon name="dragHandle" />
						Reorder
					</button>
				</li>
				<!-- <li>
					<button @click="handleHide">
						<UiIconEyeOff />
						Hide page
					</button>
				</li> -->
			</ul>
		</UiDropdown>
	</li>
</template>

<script lang="ts" setup>
export type SidebarLinkMode = "default" | "edit" | "reorder";

interface Props {
	to?: string;
	icon?: string;
	title?: string;
	active?: boolean;
	expanded?: boolean;
	mode?: SidebarLinkMode;
	dragging?: boolean;
	dragOver?: boolean;
}

withDefaults(defineProps<Props>(), {
	active: false,
	expanded: false,
	mode: "default",
	dragging: false,
	dragOver: false,
});

const emit = defineEmits<{
	more: [event: MouseEvent];
	hide: [];
	"reorder-mode": [];
	dragstart: [event: DragEvent];
	dragend: [event: DragEvent];
	dragover: [event: DragEvent];
	dragleave: [event: DragEvent];
	drop: [event: DragEvent];
}>();

const isDropdownOpen = ref(false);
const dropdownTargetId = useId();

function toggleDropdown() {
	isDropdownOpen.value = !isDropdownOpen.value;
}

function handleHide() {
	emit("hide");
	isDropdownOpen.value = false;
}

function handleReorderMode() {
	emit("reorder-mode");
	isDropdownOpen.value = false;
}

function handleDragStart(event: DragEvent) {
	emit("dragstart", event);
}

function handleDragEnd(event: DragEvent) {
	emit("dragend", event);
}

function handleDragOver(event: DragEvent) {
	emit("dragover", event);
}

function handleDragLeave(event: DragEvent) {
	emit("dragleave", event);
}

function handleDrop(event: DragEvent) {
	emit("drop", event);
}
</script>

<style lang="scss" scoped>
.sidebar-link {
	width: 100%;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	position: relative;

	&.active {
		.link-content {
			color: var(--text-50);
			border-color: var(--background-500);
			background-color: var(--background-200);
		}
	}

	&.expanded {
		.link-content {
			padding: 0.5rem 0.8rem;

			.link-title {
				position: relative;
				top: 1px;
				max-width: 120px;
				opacity: 1;
				margin-left: 0.8rem;
				min-height: 28px;
			}
		}

		.drag-handle {
			opacity: 0.6;
		}

		&:hover {
			.drag-handle {
				opacity: 1;
			}
		}
	}

	&:hover {
		.more-button,
		.drag-handle {
			opacity: 1;
		}
	}

	&.dragging {
		opacity: 0.4;

		.link-content {
			cursor: grabbing;
		}
	}

	&.drag-over {
		.link-content {
			border-color: var(--primary-500);
			background-color: var(--primary-100);
		}
	}

	&.mode-edit {
		.more-button {
			opacity: 1;
		}
	}

	&.mode-reorder {
		.drag-handle {
			opacity: 1;
		}
	}

	&.expanded.mode-edit,
	&.expanded.mode-reorder {
		.link-content {
			padding-right: 2rem;
		}
	}
}
.link-content {
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	border: 1px solid transparent;
	border-radius: 0.25rem;
	color: var(--text-400);
	fill: var(--text-400);
	width: 100%;
	box-sizing: border-box;
	padding: 0.5rem 0.5rem;
	height: 48px;
	transition:
		padding-left 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		padding-right 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		border-color 0.35s cubic-bezier(0.075, 0.82, 0.165, 1),
		background-color 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);

	&:hover {
		background-color: var(--background-200);
		color: var(--text-50);
		border-color: var(--background-500);
	}

	:deep(svg),
	:deep(img) {
		width: 26px;
		min-width: 26px;
		height: 26px;
		min-height: 26px;
		position: relative;
		top: 1px;
		color: inherit;
		fill: inherit;
	}

	.link-title {
		font-size: 1.5rem;
		font-weight: 500;
		max-width: 0;
		margin-left: 0rem;
		opacity: 0;
		color: inherit;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		transition:
			max-width 0.25s cubic-bezier(0.075, 0.82, 0.165, 1),
			margin-left 0.25s cubic-bezier(0.075, 0.82, 0.165, 1),
			opacity 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);
	}
}

.more-button,
.drag-handle {
	position: absolute;
	right: 0.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 24px;
	border-radius: 0.25rem;
	opacity: 0;
	transition:
		opacity 0.2s ease,
		background-color 0.2s ease;
	color: var(--text-400);

	:deep(svg) {
		width: 16px;
		height: 16px;
	}
}

.more-button {
	background: transparent;
	border: none;
	cursor: pointer;
	padding: 0;

	&:hover {
		background-color: var(--background-300);
		color: var(--text-50);
	}
}

.drag-handle {
	cursor: grab;

	&:active {
		cursor: grabbing;
	}
}

.dropdown-options-list svg {
	margin-right: 0.5rem;
	width: 16px;
	height: 16px;
	vertical-align: middle;
}

.dropdown-options-header {
	padding: 0.5rem 1rem;
	border-bottom: 1px solid var(--background-200);
	color: var(--text-300);
	background-color: var(--background-100);
	font-family: var(--default-font-stack);
	font-size: 0.9rem;

	i {
		font-style: normal;
		font-weight: 400;
		color: var(--text-500);
		margin-left: 0.25rem;
	}
}

:root[data-theme="dark"] {
	.sidebar-link {
		&.active {
			.link-content {
				background-color: var(--background-200);
			}

			&:hover .link-content {
				border-color: var(--background-400);
				background-color: var(--background-200);
			}
		}

		&:hover .link-content {
			border-color: var(--background-400);
			background-color: var(--background-100);
		}
	}
}

:root[data-theme="light"] {
	.sidebar-link {
		&.active {
			.link-content {
				background-color: var(--background-300);
			}

			&:hover .link-content {
				border-color: var(--background-500);
				background-color: var(--background-300);
			}
		}

		&:hover .link-content {
			border-color: var(--background-500);
			background-color: var(--background-200);
		}
	}
}
</style>
